import styles from '@/styles/job.module.scss';
import Layout from '@/layouts/page-base.layout';
import type { TSkills, TPerks, TJobDescription, TEmployer, TJob} from '@/types/job';
import { useEffect } from 'react';
import { formatMoney, openApp, getLocalesEnv } from '@/utils';
import { Ellipsis } from 'antd-mobile';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { WEB3_EVENT_ID, WEB3_EVENT_OPERATE } from '@/consts';
import Cookies from 'js-cookie';
import track from '@/utils/track';
import ServerAPI from '@/api/server-api';
import { GetServerSidePropsContext } from 'next';
import clientAPI from '@/api/client-api';

// images
import compLogImg from '@/static/images/landing/company-log.jpg';
import successFilled from '@/static/images/landing/success-filled.svg';
import remote from '@/static/images/landing/remote.svg';
import remoteBackground from '@/static/images/landing/remote-background.svg';
import location from '@/static/images/landing/location.svg';
import workType from '@/static/images/landing/work-type.svg';
import workYear from '@/static/images/landing/work-year.svg';
import education from '@/static/images/landing/education.svg';
import arrowRightWhite from '@/static/images/landing/arrow-right_white.svg';
import arrowRighGrey from '@/static/images/landing/arrow-right_grey.svg';
import chatImage from '@/static/images/landing/chat.png';
import officialAccount from '@/static/images/landing/official-account.png';
import groupChat from '@/static/images/landing/group-chat.png';

import useOpenInstall from '@/hooks/useOpenInstall';
import useChannel from '@/hooks/useChannel';
import clientUrlHelper from '@/utils/client-url';
import Head from '@/components/headv3';
import dynamic from 'next/dynamic';
import { SEOConfig, ShareConfig } from '@/types/head';
const WeChat = dynamic(import('@/components/wx'), { ssr: false })

function Company({ organizationInfo }: any) {
  const { t } = useTranslation('common');
  const expandText: string = t('VIEWALL');

  return (
    <div className={styles['company']}>
      <a href={`/project/${organizationInfo.uuid8}`}>
        <div className={styles['company-info']}>
          <div className={`${styles['company-logo']}`}>
            <div
              style={{
                backgroundImage: `url(${organizationInfo.organizationLogoUrl || compLogImg.src
                  })`,
              }}
            ></div>
          </div>

          <div className={`${styles['among']}`}>
            <div className={styles['inline-wrap']}>
              <div className={styles['block-wrap']}> </div>
              <div className={styles['company-name']}>
                {organizationInfo.organizationName}
                {/* Series A */}
                {/* fdsfds  fsdf   sdfdsfd fsd  dsfds fds fds fds fsd fsd sd  */}

                {organizationInfo.verifyStatus === 1 ? (
                  <div
                    className={`${styles['success-filled']}`}
                    style={{ backgroundImage: `url(${successFilled.src})` }}
                  ></div>
                ) : (
                  ''
                )}
              </div>
            </div>
            <div className={styles['company-other']}>
              {organizationInfo.financingStageStr ? (
                <span>{organizationInfo.financingStageStr}</span>
              ) : (
                ''
              )}
              {organizationInfo.organizationSizeStr ? (
                <span> · {organizationInfo.organizationSizeStr}</span>
              ) : (
                ''
              )}
              {organizationInfo.industryStr ? <span> · {organizationInfo.industryStr}</span> : ''}
            </div>
          </div>

          <div
            className={`${styles['company-arrows']}`}
            style={{ backgroundImage: `url(${arrowRightWhite.src})` }}
          />
        </div>
      </a>

      <div className={`${styles['company-desc']}`}>
        <Ellipsis
          rows={5}
          content={organizationInfo.shortDescription}
          direction="end"
          expandText={expandText}
        />
      </div>
    </div>
  );
}

function Job({ positionDetail }: { positionDetail: TJob }) {
  const { t } = useTranslation('common');
  return (
    <div className={styles['job']}>
      <h1 className={styles['job-name']}>{positionDetail.positionTitle}</h1>

      <div className={styles['job-salary']}>
        { positionDetail.discussInPerson === 1 ? '面议' : (
          <>
            {formatMoney(positionDetail.salaryFrom)} - {formatMoney(positionDetail.salaryTo)} {positionDetail.salaryUnitStr}/{positionDetail.salaryTimeUnitStr}
          </>
        ) }
      </div>

      <div className={styles['job-tags-wrap']}>
        <ul className={styles['job-tags']}>
          {positionDetail.isRemote === 1 ? (
            <li className={`${styles['job-tag-wrap']}`}>
              <div className={`${styles['job-tag']}`}>
                <div
                  className={`${styles['job-tag-icon']} relative`}
                  style={{ backgroundImage: `url(${remote.src})` }}
                ></div>
                <span
                  className={styles['job-tag-remote']}
                  style={{ backgroundImage: `url(${remoteBackground.src})` }}
                >
                  {t('Remote')}
                </span>
              </div>
            </li>
          ) : (
            ''
          )}

          {positionDetail.locationStr ? (
            <li className={styles['job-tag-wrap']}>
              <div className={`${styles['job-tag']}`}>
                <div
                  className={`${styles['job-tag-icon']} relative`}
                  style={{ backgroundImage: `url(${location.src})` }}
                ></div>
                {positionDetail.locationStr}
              </div>
            </li>
          ) : (
            ''
          )}

          {positionDetail.workTimeStr ? (
            <li className={styles['job-tag-wrap']}>
              <div className={`${styles['job-tag']}`}>
                <div
                  className={`${styles['job-tag-icon']} relative`}
                  style={{ backgroundImage: `url(${workType.src})` }}
                ></div>
                {positionDetail.workTimeStr}
              </div>
            </li>
          ) : (
            ''
          )}

          {positionDetail.requiredWorkYearStr ? (
            <li className={styles['job-tag-wrap']}>
              <div className={`${styles['job-tag']}`}>
                <div
                  className={`${styles['job-tag-icon']} relative`}
                  style={{ backgroundImage: `url(${workYear.src})` }}
                ></div>
                {positionDetail.requiredWorkYearStr}
              </div>
            </li>
          ) : (
            ''
          )}

          {positionDetail.requiredEducationStr ? (
            <li className={styles['job-tag-wrap']}>
              <div className={`${styles['job-tag']}`}>
                <div
                  className={`${styles['job-tag-icon']} relative`}
                  style={{ backgroundImage: `url(${education.src})` }}
                ></div>
                {positionDetail.requiredEducationStr}
              </div>
            </li>
          ) : (
            ''
          )}
        </ul>
      </div>
    </div>
  );
}

function Employer({ profile, oid }: TEmployer) {
  return (
    <a href={`/recruiter/${profile.accountUuid8}?oid=${oid}`}>
      <div className={`${styles.employer} relative`}>
        <div className={`flex`}>
          <div
            className={`${styles['employer-avatar']} relative`}
            style={{ backgroundImage: `url(${profile.formalPhotoUrl})` }}
          >
            <div className={styles['employer-online']}></div>
          </div>

          <div className={styles['employer-info']}>
            <div className={styles['employer-name']}>{profile.realName}</div>
            <div className={styles['employer-desc']}>
              {profile.organizationName} · {profile.currentTitle}
            </div>
          </div>

          <div
            className={`${styles['employer-arrows']}`}
            style={{ backgroundImage: `url(${arrowRighGrey.src})` }}
          />
        </div>
      </div>
    </a>
  );
}

function JobDescription({ text }: TJobDescription) {

  if (!text) return null

  const { t } = useTranslation('common');
  const expandText: string = t('VIEWALL');

  return (
    <div className={styles['job-description']}>
      <h3 className={styles.h3}>{t('JobDescription')}</h3>
      <div className={`${styles['job-description-content']}`}>
        <Ellipsis
          rows={20}
          content={text}
          direction="end"
          expandText={expandText}
        />
      </div>
    </div>
  );
}

function Skills({ list }: TSkills) {
  const { t } = useTranslation('common');

  return (
    <div className={styles.skills}>
      <h3 className={styles.h3}>{t('Skills')}</h3>
      <ul className={`${styles['skills-list']}`}>
        {list.map((item: string) => (
          <li key={item} className={`${styles['skills-item']}`}>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Perks({ list }: TPerks) {
  if (!list || list.length === 0) return null

  const { t } = useTranslation('common')

  return (
    <div className={styles.perks}>
      <h3 className={styles.h3}>{t('Perks')}</h3>
      <div className={styles['perks-list-wrap']}>
        <ul className={`${styles['perks-list']}`}>
          {list.map((item: any) => (
            <li key={item.id} className={`${styles['perks-item-wrap']}`}>
              <div className={styles['perks-item']}>
                <div
                  className={styles['perks-icon']}
                  style={{ backgroundImage: `url(${item.iconUrl})` }}
                ></div>
                <div className={styles['perks-text']}>{item.welfare}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Socialize() {
  return (
    <div className={`${styles['socialize']}`}>
      <div className={styles['socialize-code']}>
        <img src={groupChat.src} alt="加入 Web3 交流社群" />
      </div>
      <div className={styles['socialize-code']}>
        <img src={officialAccount.src} alt="关注 SmartDeer 公众号" />
      </div>
    </div>
  );
}

function Footer({ avatar, onClick }: any) {
  const { t } = useTranslation('common');
  const formalPhotoUrl = avatar;

  return (
    <footer className={`${styles.footer} fixed bottom-0 w-full footer`}>
      <div className={styles['footer-content']}>
        <div
          className={`${styles.avatar} relative`}
          style={{ backgroundImage: `url(${formalPhotoUrl})` }}
        >
          <div
            className={`${styles.chat}`}
            style={{ backgroundImage: `url(${chatImage.src})` }}
          ></div>
        </div>

        <div className={`${styles['footer-text']}`}>
          {t('GoToTheAPPAnd')}
          <span>{t('TalkToMeDirectlyAboutJob')}</span>
        </div>

        <button className={styles.btn} onClick={onClick}>
          {t('OPENAPP')}
        </button>
      </div>
    </footer>
  );
}

function getSalaryStr(position:any, lang: 'en' | 'zh') {
  let salaryStr = '面议'
  if (position.discussInPerson == 1) {
    salaryStr = lang == 'en' ? 'Open' : '面议'
  } else {
    salaryStr = `${formatMoney(position.salaryFrom)}-${formatMoney(position.salaryTo)} ${position.salaryUnitStr
    }/${position.salaryTimeUnitStr}`
  }
  return salaryStr
}

export async function getServerSideProps(props: GetServerSidePropsContext) {
  const start = new Date().getTime();

  const { req, res, params, query } = props;

  const Language = getLocalesEnv(req, query);
  const i18n = await serverSideTranslations(Language, ['common']);

  // 初始化API
  const API = new ServerAPI({ acceptLanguage: Language });
  // 获取数据
  if (!params?.id) return { notFound: true };
  const id = params.id as string;
  const response = await API.landing.getPosition(id);
  const positionDetail = response.data;
  if (!positionDetail) return { notFound: true };

  const organizationEntity = positionDetail.organizationEntity
  const organizationAccountProfile = positionDetail.organizationAccountProfile

  const channelCode =  query.channelCode || query._ch
  const pageTitle = Language == 'en'
    ? `[SmartDeer] ${organizationEntity.organizationName}·${organizationAccountProfile.realName} is hiring for ${positionDetail.positionTitle}, please open the link and chat directly!`
    : `【SmartDeer】${organizationEntity.organizationName}·${organizationAccountProfile.realName}正在招聘${positionDetail.positionTitle}，请打开链接直接开聊！`

  const salaryStr = getSalaryStr(positionDetail, Language)

  const pageDescription = `${salaryStr} | ${positionDetail.requiredWorkYearStr} | ${positionDetail.workTimeStr
      } | ${positionDetail.requiredEducationStr}`

  const pageUrl = 'https://' + req.headers.host + req.url

  let share:ShareConfig = {
    title: pageTitle,
    description: pageDescription,
    image: positionDetail.share.h5.default.shareImg,
    url: pageUrl,
    thumbnail: organizationEntity.organizationLogoUrl
  }

  if (channelCode) {
    const data = await API.position.getPositionShareConfig(channelCode as string)
    if (data) share = {
      title: data[Language]?.title || share.title,
      description: data[Language]?.description || share.description,
      image: data[Language]?.image || share.image,
      url: data[Language]?.url || share.url,
      thumbnail: data[Language]?.thumbnail || share.thumbnail
    }
  }

  const seo = {
    title: pageTitle,
    description: pageDescription,
    keywords: ''
  }

  // 初始化语言包

  const end = new Date().getTime();
  res.setHeader('X-TIME-SPAN', end - start);

  return {
    props: {
      seo,
      share,
      positionDetail,
      params,
      lang: Language,
      ...i18n,
    },
  };
}

interface PageProps {
  params: any
  positionDetail: any
  share: ShareConfig,
  seo: SEOConfig
}

const Page = (props: PageProps) => {
  const { params, positionDetail, share, seo } = props;

  const organizationInfo = positionDetail.organizationEntity
  const appUrl = positionDetail.share.h5.default.universeUrl
  const openinstall = useOpenInstall();
  const channel = useChannel()
  const clientUrl = clientUrlHelper.positionDetailUrl(params.id)

  useEffect(() => {
    if (!Cookies.get('WEB3_EVENT_JOB')) {
      Cookies.set('WEB3_EVENT_JOB', WEB3_EVENT_ID);
      clientAPI.events.setOrganizationPopular(WEB3_EVENT_ID, {
        score: WEB3_EVENT_OPERATE.job.score,
        organizationUuid8: positionDetail.organizationEntity.uuid8,
        operateType: WEB3_EVENT_OPERATE.job.type,
        openId: '',
      });
    }
  }, []);

  const handleClickToApp = () => {
    track.click('footer_open_app_click', {channelCode: channel?.channel, shareCode: channel?.shareCode});
    openinstall?.wakeupOrInstall({channelCode: channel?.channel, data: { path: clientUrl, shareCode:channel?.shareCode } });
    openApp(appUrl);
  };

  return (
    <Layout pagename='share_job'>
      {/* head meta */}
      <Head seo={seo} share={share}></Head>

      {/* wechat share */}
      <WeChat share={share} />

      <Company organizationInfo={organizationInfo} />
      <div className={styles['main']}>
        <Job positionDetail={positionDetail} />
        <Employer profile={positionDetail.organizationAccountProfile} oid={organizationInfo.uuid8} />
        <JobDescription text={positionDetail.positionDescription} />
        <Perks list={positionDetail.positionWelfareObj} />
        <Socialize />
      </div>
      <Footer avatar={positionDetail.organizationAccountProfile.formalPhotoUrl} onClick={handleClickToApp} />
    </Layout>
  );
};

export default Page;
